import * as React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { Typography, Stack, Skeleton } from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import { useQuery } from '@tanstack/react-query';
import { useLocation } from "react-router-dom";

// Components
import Header from "../components/common/header/Header";
import Footer from "../components/common/Footer/Footer";
import ProductCategories from "../components/products/ProductCategories";
import ProductBrands from "../components/products/ProductBrands";
import ProductItem from "../components/products/ProductItem";
import ProductCriteria from "../components/products/ProductCriteria";
import InStockOnlyFilter from "../components/products/InStockOnlyFilter";
import CompareProducts from "../components/products/CompareProducts";
import CustomBreadcrumbs from "../components/common/Breadcrumbs/Breadcrumbs";

// Services & Interfaces
import { fetchArticles, fetchGenericArticleFacets } from "../services/tecdocService";
import { Article } from "../interfaces/Article";
import { CriteriaFilters } from "../interfaces/CriteriaFilters";
import { useProgressiveInStockFilter } from "../hooks/useProgressiveInStockFilter";
import theme from "../theme";

/** Parse search-products URL so first paint matches the query string (avoids a flash of "no results" before useEffect). */
function parseProductSearchParams(search: string) {
  const urlParams = new URLSearchParams(search);
  const criteriaParams: CriteriaFilters[] = [];
  urlParams.forEach((value, key) => {
    if (key === "criteria") {
      const [criteriaId, rawValue] = value.split(":");
      criteriaParams.push({
        criteriaId: parseInt(criteriaId, 10),
        rawValue,
      });
    }
  });
  return {
    keyword: urlParams.get("keyword") ?? "",
    categoryId: urlParams.get("category") ? parseInt(urlParams.get("category")!, 10) : null,
    brandId: urlParams.get("brand") ? parseInt(urlParams.get("brand")!, 10) : null,
    linkageTargetType: urlParams.get("linkageTargetType"),
    linkageTargetId: urlParams.get("linkageTargetId")
      ? parseInt(urlParams.get("linkageTargetId")!, 10)
      : null,
    assemblyGroupNodeIds: urlParams.get("assemblyGroupNodeIds")
      ? parseInt(urlParams.get("assemblyGroupNodeIds")!, 10)
      : null,
    criteriaFilters: criteriaParams,
    inStockOnly: urlParams.get("inStockOnly") === "1",
  };
}

const AvailabilityScanFooter = () => (
  <Box sx={{ py: 2, textAlign: "center" }}>
    <Skeleton animation="wave" sx={{ width: "180px", mx: "auto" }} />
    <Typography sx={{ mt: 1, fontSize: "13px", fontFamily: "Inter", color: "text.secondary" }}>
      Verfügbarkeit wird geprüft...
    </Typography>
  </Box>
);

const ProductSearchPage = () => {
  const location = useLocation();

  // Seed state once from the URL so the first paint enables the query (avoids "no results" before hydration).
  const initialSearch = React.useMemo(
    () => parseProductSearchParams(location.search),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only initial URL snapshot for useState seeds
    []
  );

  const [categoryId, setCategoryId] = React.useState<number | null>(initialSearch.categoryId);
  const [brandId, setBrandId] = React.useState<number | null>(initialSearch.brandId);
  const [linkageTargetId, setLinkageTargetId] = React.useState<number | null>(initialSearch.linkageTargetId);
  const [linkageTargetType, setLinkageTargetType] = React.useState<string | null>(initialSearch.linkageTargetType);
  const [assemblyGroupNodeIds, setAssemblyGroupNodeIds] = React.useState<number | null>(
    initialSearch.assemblyGroupNodeIds
  );
  const [criteriaFilters, setCriteriaFilters] = React.useState<CriteriaFilters[]>(initialSearch.criteriaFilters);
  const [inStockOnly, setInStockOnly] = React.useState(initialSearch.inStockOnly);
  const [comparisonArticles, setComparisonArticles] = React.useState<Article[]>([]);
  const [page, setPage] = React.useState(1);
  const [keyword, setKeyword] = React.useState(initialSearch.keyword);

  // Keep state in sync when the URL changes (client navigation or back/forward)
  React.useEffect(() => {
    const p = parseProductSearchParams(location.search);
    setKeyword(p.keyword);
    setCategoryId(p.categoryId);
    setBrandId(p.brandId);
    setLinkageTargetType(p.linkageTargetType);
    setLinkageTargetId(p.linkageTargetId);
    setAssemblyGroupNodeIds(p.assemblyGroupNodeIds);
    setCriteriaFilters(p.criteriaFilters);
    setInStockOnly(p.inStockOnly);
    setPage(1);
  }, [location.search]);

  // React Query hooks
  const { data: genericArticleFacets } = useQuery({
    queryKey: ['genericArticleFacets', keyword, brandId, linkageTargetType, linkageTargetId, assemblyGroupNodeIds],
    queryFn: () => fetchGenericArticleFacets(keyword, {
      brand: brandId,
      linkageTargetType,
      linkageTargetId,
      assemblyGroupNodeIds
    }),
    enabled: !!(keyword || brandId || assemblyGroupNodeIds)
  });

  const articlesQueryEnabled = !!(keyword || categoryId || brandId || assemblyGroupNodeIds);

  const { data: articlesData, isPending, isFetching } = useQuery({
    queryKey: ['articles', keyword, categoryId, brandId, linkageTargetType, linkageTargetId, assemblyGroupNodeIds, criteriaFilters, page],
    queryFn: () => fetchArticles(keyword, {
      category: categoryId,
      brand: brandId,
      linkageTargetType,
      linkageTargetId,
      assemblyGroupNodeIds,
      criteria: criteriaFilters,
      page
    }),
    enabled: articlesQueryEnabled
  });

  const articles = React.useMemo(
    () => articlesData?.articles ?? [],
    [articlesData?.articles]
  );

  const { displayArticles, isScanning, hasScannedAll, loadMore } = useProgressiveInStockFilter(
    articles,
    inStockOnly
  );

  /** While fetching an empty or missing result set, keep skeleton — avoids "Keine Produkte" during refetch. */
  const showArticlesSkeleton =
    articlesQueryEnabled &&
    (isPending ||
      (isFetching &&
        (!articlesData || (articlesData.articles?.length ?? 0) === 0)));

  const showArticlesEmpty =
    articlesQueryEnabled &&
    !showArticlesSkeleton &&
    !!articlesData &&
    articlesData.articles.length === 0;

  const showAvailabilityFilterSkeleton =
    articlesQueryEnabled &&
    !showArticlesSkeleton &&
    articles.length > 0 &&
    inStockOnly &&
    displayArticles.length === 0 &&
    isScanning;

  const showNoAvailableArticles =
    articlesQueryEnabled &&
    !showArticlesSkeleton &&
    !showAvailabilityFilterSkeleton &&
    articles.length > 0 &&
    inStockOnly &&
    hasScannedAll &&
    displayArticles.length === 0;

  // Memoized callbacks
  const updateCategories = React.useCallback((newCategoryId: number) => {
    setCategoryId(newCategoryId);
    setPage(1);
  }, []);

  const updateCriteriaFilters = React.useCallback((criteriaId: number, rawValue: string, isChecked: boolean) => {
    setCriteriaFilters(prevFilters => {
      const updatedFilters = [...prevFilters];
      if (isChecked) {
        updatedFilters.push({ criteriaId, rawValue });
      } else {
        return updatedFilters.filter(
          filter => filter.criteriaId !== criteriaId || filter.rawValue !== rawValue
        );
      }
      return updatedFilters;
    });
    setPage(1);
  }, []);

  const updateComparisonArticles = React.useCallback((article: Article, isChecked: boolean) => {
    setComparisonArticles(prevArticles => 
      isChecked 
        ? [...prevArticles, article]
        : prevArticles.filter(a => a !== article)
    );
  }, []);

  const updateInStockOnly = React.useCallback((checked: boolean) => {
    setInStockOnly(checked);
  }, []);

  const renderProductItem = React.useCallback((index: number) => {
    if (!displayArticles.length || index >= displayArticles.length) return null;

    const article = displayArticles[index];
    return (
      <ProductItem
        key={`${article.articleNumber}-${article.dataSupplierId}-${index}`}
        article={article}
        onToggle={updateComparisonArticles}
      />
    );
  }, [displayArticles, updateComparisonArticles]);

  const virtuosoFooter = React.useCallback(() => {
    if (!inStockOnly || !isScanning || displayArticles.length === 0) {
      return null;
    }
    return <AvailabilityScanFooter />;
  }, [displayArticles.length, inStockOnly, isScanning]);

  const renderSkeleton = React.useMemo(() => (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Box sx={{
        flexGrow: 1,
        m: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12}>
            <Box>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={12} md={7} sx={{paddingLeft: '0px !important', paddingTop: '0px !important'}}>
                  <CustomBreadcrumbs />
                </Grid>
                <Grid item xs={12} sm={12} md={5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                    <Box sx={{ flexGrow: 1, marginRight: 2 }}>
                      <Skeleton animation="wave" sx={{ width: '200px', height: '30px' }} />
                    </Box>
                    <Box sx={{ flexGrow: 3, marginRight: 2 }}>
                      <Skeleton animation="wave" sx={{ width: '200px', height: '30px' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton animation="wave" sx={{ width: '200px', height: '30px' }} />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ mt: 4 }}>
              <ProductItem />
            </Box>
          </Grid>
        </Grid>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <Skeleton animation="wave" sx={{ width: '100px' }} />
        </Stack>
      </Box>
    </Stack>
  ), []);

  return (
    <>
      <Header />
      <Box sx={{
        flexGrow: 1,
        m: 2,
        minHeight: '71vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={2.5}>
            {articlesQueryEnabled && (
              <InStockOnlyFilter
                inStockOnly={inStockOnly}
                onInStockOnlyChange={updateInStockOnly}
              />
            )}
            <ProductCategories 
              genericArticles={genericArticleFacets || []} 
              onCategoryChange={updateCategories}
            />
            {articlesQueryEnabled && categoryId !== null && (
              <ProductCriteria
                groupedCriteria={articlesData?.groupedCriteria}
                onCriteriaChange={updateCriteriaFilters}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={12} md={9.5}>
            <Box sx={{
              bgcolor: "#f1f1f1",
              py: 1,
              px: 2,
              borderTop: `2px solid ${theme.palette.primary.dark}`,
            }}>
              <Grid container spacing={1}>
                  <Grid item xs={12} sm={12} md={5} sx={{paddingLeft: '0px !important'}}>
                    {articlesData?.assemblyGroupFacets && (
                      <CustomBreadcrumbs assemblyGroupFacets={articlesData.assemblyGroupFacets.counts} />
                    )}
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Box sx={{ display: 'flex', justifyContent: { xs: "flex-start", sm: "flex-end"}, pr: 1 }}>
                      <Box sx={{ mr:1, display: {xs: 'none', sm: 'block', md: 'block', lg: 'block'} }}>
                        <CompareProducts compareArticles={comparisonArticles}/>
                      </Box>
                      {articlesData?.dataSupplierFacets && (
                        <Box sx={{ mr:1 }}>
                          <ProductBrands dataSupplierFacets={articlesData.dataSupplierFacets} />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>

            </Box>
            <Box sx={{ mt: 4 }}>
              {showArticlesSkeleton || showAvailabilityFilterSkeleton ? (
                renderSkeleton
              ) : !articlesQueryEnabled ? null : showArticlesEmpty ? (
                <Typography variant="h5" textAlign="center" mt={10}>
                  Keine Produkte gefunden.
                </Typography>
              ) : showNoAvailableArticles ? (
                <Typography variant="h5" textAlign="center" mt={10}>
                  Keine verfügbaren Produkte gefunden.
                </Typography>
              ) : displayArticles.length ? (
                <Virtuoso
                  useWindowScroll
                  style={{ height: "calc(100vh - 150px)", width: "100%" }}
                  totalCount={displayArticles.length}
                  itemContent={renderProductItem}
                  endReached={inStockOnly ? loadMore : undefined}
                  overscan={600}
                  increaseViewportBy={{ top: 600, bottom: 600 }}
                  initialTopMostItemIndex={0}
                  defaultItemHeight={200}
                  components={{ Footer: virtuosoFooter }}
                />
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Footer />
    </>
  );
};

export default ProductSearchPage;
